import dayjs from 'dayjs';
import { memo } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import TextField from 'components/text-field';

import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { UpdateManualInventoryInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.feedInventoryTab';

interface Props {
  isDialog: boolean;
  control: Control<UpdateManualInventoryInput, any>;
  setValue?: UseFormSetValue<UpdateManualInventoryInput>;
}

const EditFeedInventory: React.FC<Props> = ({ isDialog, control, setValue }) => {
  const intl = useIntl();
  const theme = useTheme();
  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const watchDelivery = useWatch({
    control,
    name: 'afterDelivery',
  });

  if (!ofdSettings) return null;

  const { manualInventory, deliveryExpected, currentConsumptionRate, deliveredQuantity } = ofdSettings;

  const startInventory = ofdSettings.inventoryExplanation.startingInventory ?? 0;

  // #region HANDLERS

  const handleDeliveryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    !!setValue && setValue('afterDelivery', value === 'true' ? true : value === 'false' ? false : null);
  };

  // #enregion

  return (
    <>
      {!deliveryExpected ? (
        <>
          <Box>
            <Typography variant="body1" mb={4}>
              <FormattedMessage id={`${PAGE_NAME_ID}.inventoryLevelNoDeliveryLabel`} />({dayjs().format('DD MMM')}) :
            </Typography>
            <TextField
              dark
              fullWidth
              type="number"
              controller={{
                control,
                name: 'inventory',
                rules: {
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
              endAdornment="kg"
              label={''}
            />

            <Box display={'flex'} justifyContent={'space-between'} mt={4} p={4} sx={{ background: '#F7F8F8' }}>
              <Typography>
                <FormattedMessage id={`${PAGE_NAME_ID}.estimatedSubtractionNoDeliveryLabel`} />
              </Typography>
              <Typography>
                -
                <FormattedNumber
                  value={currentConsumptionRate}
                  unit={'kilogram'}
                  style="unit"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Typography>
            </Box>
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              m={theme.spacing(8, 0)}
              p={4}
              sx={{ background: theme.palette.green.o25 }}>
              <Typography maxWidth={230}>
                <FormattedMessage id={`${PAGE_NAME_ID}.finalCalculatedLabel`} />
              </Typography>
              <Typography variant="h4">
                <FormattedNumber value={startInventory - Number(currentConsumptionRate)} maximumFractionDigits={0} />
                kg
              </Typography>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box>
            {isDialog && (
              <Typography variant="body1" mb={5}>
                <FormattedMessage id={`${PAGE_NAME_ID}.headerText`} />
              </Typography>
            )}
            <Typography variant="body1" mb={4} fontWeight={'bold'}>
              <FormattedMessage id={`${PAGE_NAME_ID}.inventoryLevelLabel`} />({dayjs().format('DD MMM')}) :
            </Typography>
            <TextField
              dark
              fullWidth
              type="number"
              controller={{
                control,
                name: 'inventory',
                rules: {
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
              endAdornment="kg"
              label={''}
            />
            <Box display={'flex'} justifyContent={'space-between'} mt={8} p={4} sx={{ background: '#F7F8F8' }}>
              <Typography>
                <FormattedMessage id={`${PAGE_NAME_ID}.estimatedSubtractionLabel`} />
              </Typography>
              <Typography>
                -
                <FormattedNumber
                  value={currentConsumptionRate}
                  unit={'kilogram'}
                  style="unit"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.palette.darkBlue.o25, margin: theme.spacing(8, 0) }} />

          <Box display={'flex'} gap={2}>
            <Typography variant="body1" fontWeight={600} mb={1}>
              <FormattedMessage id={`${PAGE_NAME_ID}.feedingStatusLabel`} />
            </Typography>
            <Typography color={theme.palette.darkBlue.o75}>
              <FormattedMessage id={`${PAGE_NAME_ID}.feedingStatusDiscloser`} />
            </Typography>
          </Box>
          <FormControl variant={'standard'}>
            <RadioGroup value={watchDelivery} onChange={handleDeliveryChange}>
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.beforeDelivery` })}
              />
              <FormControlLabel value={true} control={<Radio />} label={intl.formatMessage({ id: `${PAGE_NAME_ID}.afterDelivery` })} />
            </RadioGroup>
          </FormControl>
          {watchDelivery === false && (
            <Box display={'flex'} justifyContent={'space-between'} mt={4} p={4} sx={{ background: '#F7F8F8' }}>
              <Typography>
                <FormattedMessage id={`${PAGE_NAME_ID}.confirmedDeliveryLabel`} />
              </Typography>
              <Typography>
                +<FormattedNumber value={Number(deliveredQuantity)} unit={'kilogram'} style="unit" maximumFractionDigits={0} />
              </Typography>
            </Box>
          )}
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            m={theme.spacing(8, 0)}
            p={4}
            borderRadius={2}
            sx={{ background: theme.palette.green.o25 }}>
            <Typography maxWidth={230}>
              <FormattedMessage id={`${PAGE_NAME_ID}.finalCalculatedLabel`} />
            </Typography>
            <FormattedNumber
              maximumFractionDigits={0}
              unit={'kilogram'}
              style="unit"
              value={
                watchDelivery === false
                  ? startInventory - Number(currentConsumptionRate) + Number(deliveredQuantity)
                  : startInventory - Number(currentConsumptionRate)
              }
            />
          </Box>
        </>
      )}
    </>
  );
};

export default memo(EditFeedInventory);
